import moment from "moment";
import React, { isValidElement, useEffect, useRef, useState } from "react";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { formatValue } from "../../services/Functions";
import { Button } from "../Button/index";
import ImgWithLoading from "../ImgWithLoading/ImgWithLoading";
import TableMainContainer from "./styles";

interface Column {
  key: string;
  label: string | (() => JSX.Element);
  mask?: (value: any, item?: any) => string | JSX.Element;
  allowHTML?: boolean;
  sortKey?: string;
}

export interface SortConfig {
  keys: string;
  direction: "ascending" | "descending";
}

interface TableProps {
  data: { [key: string]: any }[];
  columns: Column[];
  sortable: boolean;
  selectable: boolean;
  button: boolean;
  buttonText?: string;
  mostrarTotais?: boolean;
  statusColor?: string;
  onDoubleClicked?: (item: any, event: React.MouseEvent) => void;
  onButtonClicked?: (item: any, event: React.MouseEvent) => void;
  onItemSelected?: (selectedItens: any[]) => void;
  onSortedDataChange?: (sortedData: any[]) => void;
  page: number;
  itensPorPagina: number;
  className?: string;
  /** quando true, a tabela limita a altura à viewport (descontando o footer fixo) e rola internamente,
   *  mantendo a barra de scroll horizontal sempre visível mesmo em telas baixas (ex.: 1366x768). */
  fitToViewportHeight?: boolean;
}

const Table: React.FC<TableProps> = ({
  data,
  columns,
  sortable,
  selectable,
  button,
  mostrarTotais,
  buttonText,
  statusColor,
  onItemSelected,
  onSortedDataChange,
  page,
  itensPorPagina,
  onButtonClicked,
  onDoubleClicked,
  className,
  fitToViewportHeight = false,
}) => {
  const { theme } = useTheme();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [tableMaxHeight, setTableMaxHeight] = useState<string>("none");

  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [sortedData, setSortedData] = useState(
    data.slice(
      (page - 1) * itensPorPagina,
      itensPorPagina === -1 ? data.length + 1 : page * itensPorPagina,
    ),
  );

  const requestSort = (keys: string, direc?: "ascending" | "descending") => {
    let direction: "ascending" | "descending" = direc || "ascending";
    if (
      sortConfig &&
      sortConfig.keys === keys &&
      sortConfig.direction === "ascending" &&
      !direc
    ) {
      direction = "descending";
    }
    setSortConfig({ keys, direction });

    if (sortable) {
      const newSortedData = [...data].sort((a, b) => {
        for (const key of keys.split(",")) {
          const trimmedKey = key.trim();

          // Check if both values are null or empty, if so, continue to next iteration
          if (
            (a[trimmedKey] === null || a[trimmedKey] === "") &&
            (b[trimmedKey] === null || b[trimmedKey] === "")
          ) {
            continue;
          }

          // If a[trimmedKey] is null or empty, b[trimmedKey] should be considered greater
          if (a[trimmedKey] === null || a[trimmedKey] === "") {
            return direction === "ascending" ? -1 : 1;
          }

          // If b[trimmedKey] is null or empty, a[trimmedKey] should be considered greater
          if (b[trimmedKey] === null || b[trimmedKey] === "") {
            return direction === "ascending" ? 1 : -1;
          }

          if (a[trimmedKey] !== b[trimmedKey]) {
            // Try to parse the fields as dates
            const aDate = moment(a[trimmedKey], moment.ISO_8601, true);
            const bDate = moment(b[trimmedKey], moment.ISO_8601, true);

            if (aDate.isValid() && bDate.isValid()) {
              // The fields are valid dates
              return direction === "ascending"
                ? aDate.diff(bDate)
                : bDate.diff(aDate);
            } else if (!isNaN(a[trimmedKey]) && !isNaN(b[trimmedKey])) {
              // The fields are numeric
              return direction === "ascending"
                ? Number(a[trimmedKey]) - Number(b[trimmedKey])
                : Number(b[trimmedKey]) - Number(a[trimmedKey]);
            } else {
              // The fields are strings
              const comp = a[trimmedKey]
                .toString()
                .localeCompare(b[trimmedKey].toString(), undefined, {
                  numeric: true,
                  sensitivity: "base",
                });
              return direction === "ascending" ? comp : -comp;
            }
          }
        }
        return 0;
      });

      setSortedData(
        newSortedData.slice(
          (page - 1) * itensPorPagina,
          itensPorPagina === -1 ? sortedData.length + 1 : page * itensPorPagina,
        ),
      );

      onSortedDataChange && onSortedDataChange(newSortedData);
    }
  };

  const selectAll = (selected: boolean) => {
    if (!selectable) return;
    setSelectedRows(
      selected ? new Set(sortedData.map((item, index) => index)) : new Set(),
    );
  };

  const selectRow = (
    index: number,
    selected: boolean,
    shiftKey: boolean,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (!selectable) return;
    const newSelectedRows = new Set(selectedRows);

    if (shiftKey) {
      const min = Math.min(...Array.from(selectedRows));
      const max = Math.max(...Array.from(selectedRows));

      if (index < min) {
        for (let i = index; i < min; i++) {
          newSelectedRows.add(i);
        }
      } else if (index > max) {
        for (let i = index; i > max; i--) {
          newSelectedRows.add(i);
        }
      }
    } else {
      if (selected) {
        newSelectedRows.add(index);
      } else {
        newSelectedRows.delete(index);
      }
    }

    setSelectedRows(newSelectedRows);
  };

  useEffect(() => {
    if (data.length > 0) {
      setSelectedRows(new Set()); // reset selected rows whenever data changes

      if (sortable && columns[0]) {
        if (sortConfig) {
          // Reaplicar ordenação existente aos novos dados
          requestSort(sortConfig.keys, sortConfig.direction);
        } else {
          // Aplicar ordenação padrão
          const sortKey = columns[0].sortKey || columns[0].key;
          const defaultDirection = sortKey === 'id_pedido' ? 'descending' : 'ascending';
          requestSort(sortKey, defaultDirection);
        }
      } else {
        setSortedData(
          data.slice(
            (page - 1) * itensPorPagina,
            itensPorPagina === -1 ? data.length : page * itensPorPagina,
          ),
        );
        onSortedDataChange && onSortedDataChange(data);
      }
    } else {
      setSortedData([]);
      onSortedDataChange && onSortedDataChange([]);
    }
  }, [data]);

  useEffect(() => {
    setSelectedRows(new Set()); // reset selected rows whenever data changes
    setSortedData(
      data.slice(
        (page - 1) * itensPorPagina,
        itensPorPagina === -1 ? sortedData.length + 1 : page * itensPorPagina,
      ),
    );
  }, [page]);

  useEffect(() => {
    if (sortedData.length > 0) {
      onItemSelected &&
        onItemSelected(
          sortedData.filter((item, index) => selectedRows.has(index)),
        );
    }
  }, [selectedRows, sortedData]);

  useEffect(() => {
    if (!fitToViewportHeight) return;
    const calcHeight = () => {
      const el = scrollRef.current;
      //não calcula se a tabela estiver oculta (ex.: aba inativa) — mediria posição errada
      if (!el || el.offsetParent === null) return;
      //topo relativo ao DOCUMENTO (não à viewport): estável mesmo com a página rolada,
      //evita o bug do F5 restaurar o scroll e calcular uma altura enorme
      const top = el.getBoundingClientRect().top + window.scrollY;
      const footer = document.querySelector(".container-footer-paginas");
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
      //folga maior para a barra de scroll horizontal ficar bem acima do footer,
      //sem precisar rolar a página pra baixo (ex.: notebook 1366x768)
      const margem = 5;
      setTableMaxHeight(`${window.innerHeight - top - footerHeight - margem}px`);
    };
    calcHeight();
    const raf = requestAnimationFrame(calcHeight);
    window.addEventListener("resize", calcHeight);
    //ResizeObserver: recalcula quando a aba sai do oculto e ganha layout, ou quando
    //o conteúdo acima muda de altura — cobre o caso da tabela ser uma aba aninhada
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => calcHeight());
      if (scrollRef.current) observer.observe(scrollRef.current);
      observer.observe(document.body);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", calcHeight);
      observer?.disconnect();
    };
    //recalcula também quando os dados aparecem / mudam de tamanho
  }, [fitToViewportHeight, sortedData.length]);

  return (
    <>
      <TableMainContainer
        className={`${className ? className : ""}`}
        theme={theme}
      >
        {sortedData.length > 0 ? (
          <div
            className={`scrollBonitoBottom${fitToViewportHeight ? " scrollBonito fitViewport" : ""}`}
            ref={fitToViewportHeight ? scrollRef : undefined}
            style={fitToViewportHeight ? { maxHeight: tableMaxHeight } : undefined}
          >
            <div className="tableContainer">
              <table>
                <thead>
                  <tr>
                    {selectable && (
                      <th>
                        <label className="checkboxBonito checkboxTable">
                          <input
                            type="checkbox"
                            checked={
                              sortedData.length > 0 &&
                              selectedRows.size === sortedData.length
                            }
                            onChange={(e) => selectAll(e.target.checked)}
                          />
                        </label>
                      </th>
                    )}
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        onClick={() => sortable && requestSort(column.sortKey || column.key)}
                      >
                        <div className="th-wrapper fonteTexto">
                          {typeof column.label === 'function' ? column.label() : column.label}
                          {typeof column.label !== 'function' && (
                            <svg
                              className={`arrow ${
                                sortConfig?.keys === (column.sortKey || column.key)
                                  ? sortConfig?.direction === "ascending"
                                    ? "down"
                                    : "up"
                                  : ""
                              }`}
                              width="8"
                              height="5"
                              viewBox="0 0 8 5"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.77552 0.220682C7.63333 0.0793364 7.44099 0 7.2405 0C7.04001 0 6.84766 0.0793364 6.70547 0.220682L3.98103 2.90718L1.29453 0.220682C1.15234 0.0793364 0.959994 0 0.759504 0C0.559014 0 0.36667 0.0793364 0.224481 0.220682C0.15335 0.291231 0.0968926 0.375166 0.0583643 0.467645C0.0198361 0.560124 0 0.659316 0 0.7595C0 0.859683 0.0198361 0.958876 0.0583643 1.05135C0.0968926 1.14383 0.15335 1.22777 0.224481 1.29832L3.44221 4.51605C3.51276 4.58718 3.59669 4.64363 3.68917 4.68216C3.78165 4.72069 3.88084 4.74053 3.98103 4.74053C4.08121 4.74053 4.1804 4.72069 4.27288 4.68216C4.36536 4.64363 4.4493 4.58718 4.51985 4.51605L7.77552 1.29832C7.84665 1.22777 7.90311 1.14383 7.94163 1.05135C7.98016 0.958876 8 0.859683 8 0.7595C8 0.659316 7.98016 0.560124 7.94163 0.467645C7.90311 0.375166 7.84665 0.291231 7.77552 0.220682Z"
                                fill="#C7C7C7"
                              />
                            </svg>
                          )}
                        </div>
                      </th>
                    ))}
                    {statusColor && <th className="buttonTH fonteTexto">Status</th>}
                    {button && (
                      <th className="buttonTH fonteTexto">
                        {buttonText ? buttonText : "Detalhes"}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedData &&
                    sortedData.map((item, index) => (
                      <tr
                        key={(page - 1) * itensPorPagina + index}
                        onClick={(e) =>
                          selectRow(
                            index,
                            !selectedRows.has(index),
                            e.shiftKey,
                            e,
                          )
                        }
                        onDoubleClick={(e) =>
                          onDoubleClicked && onDoubleClicked(item, e)
                        }
                        className={`${
                          selectedRows.has(index) && selectable && "selected"
                        }`}
                      >
                        {selectable && (
                          <td>
                            <label className="checkboxBonito">
                              <input
                                type="checkbox"
                                checked={selectedRows.has(index)}
                                onChange={(e) => e.preventDefault()}
                                onClick={(e) =>
                                  selectRow(
                                    index,
                                    e.currentTarget.checked,
                                    e.shiftKey,
                                    e,
                                  )
                                }
                              />
                            </label>
                          </td>
                        )}
                        {columns.map((column, colIndex) => {
                          if (/^(foto|imagem|image)(\s*\d+)?$/i.test(column.key)) {
                            const fotos = String(item[column.key] || "")
                              .split(";")
                              .map((f) => f.trim())
                              .filter(Boolean);
                            // "Foto" (sem número) ou "Foto 1" é a coluna principal:
                            // quando não há foto, mostra a imagem default (fallback).
                            const numeroFoto = column.key.match(/(\d+)\s*$/);
                            const ehPrimeiraFoto = !numeroFoto || numeroFoto[1] === "1";
                            return (
                              <td
                                key={
                                  (page - 1) * itensPorPagina + index + colIndex
                                }
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "4px",
                                  }}
                                >
                                  {fotos.length > 0 ? (
                                    fotos.map((foto, fotoIndex) => (
                                      <ImgWithLoading
                                        key={fotoIndex}
                                        imageClass="imagem"
                                        loadingClass="imagem"
                                        src={foto}
                                        alt={foto}
                                      />
                                    ))
                                  ) : ehPrimeiraFoto ? (
                                    <ImgWithLoading
                                      imageClass="imagem"
                                      loadingClass="imagem"
                                      src=""
                                      alt=""
                                    />
                                  ) : (
                                    <span>-</span>
                                  )}
                                </div>
                              </td>
                            );
                          } else {
                            return (
                              <td
                                className="fonteTexto"
                                key={(page - 1) * itensPorPagina + index + colIndex}
                              >
                                {(() => {
                                  let keys = column.key.split(",");
                                  let value: string | JSX.Element = "";
                                  if(keys.length > 1){
                                    value = keys.map((key) => item[key.trim()] || "").filter((val) => val !== "").join(", ");
                                  } else {
                                    value = item[column.key] || "";
                                  }

                                  if (column.mask) {
                                    value = column.mask(value, item);
                                  }

                                  return column.allowHTML ? <span dangerouslySetInnerHTML={{ __html: value as string }} /> : value;
                                })()}
                              </td>
                            );
                          }
                        })}
                        {statusColor && (
                          <td className="stickyTD">
                            <div
                              className="bolinha"
                              style={{ backgroundColor: statusColor }}
                            ></div>
                          </td>
                        )}
                        {button && (
                          <td className="stickyTD">
                            <Button
                              onClick={(e) =>
                                onButtonClicked && onButtonClicked(item, e)
                              }
                              className="buttonTable fonteTexto"
                              padding="5rem"
                              title={buttonText ? buttonText : "Detalhes"}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
                {(mostrarTotais) && (
                  <tfoot>
                    <tr>
                      {columns.map((column, index) => {
                        let total = 0;
                        const keyMapping = [
                          { key: "Estoque", isMonetary: false, decimalPlaces: 0 },
                          { key: "estoque_atual", isMonetary: true, decimalPlaces: 0 },
                          { key: "Peso", isMonetary: false, decimalPlaces: 2 },
                          { key: "Peso (g)", isMonetary: false, decimalPlaces: 2 },
                          { key: "Preço:", isMonetary: true, decimalPlaces: 2 },
                          { key: "Preços", isMonetary: true, decimalPlaces: 2 },
                          { key: "quantidade", isMonetary: true, decimalPlaces: 2 },
                          { key: "Quantidade de Pedidos", isMonetary: false, decimalPlaces: 0 },
                          { key: "Quantidade Vendida de Produtos", isMonetary: false, decimalPlaces: 0 },
                          { key: "Quantidade vendida", isMonetary: false, decimalPlaces: 0 },
                          { key: "Quantidade inserida", isMonetary: false, decimalPlaces: 0 },
                          { key: "Quantidade retirada/ajustada", isMonetary: false, decimalPlaces: 0 },
                          { key: "Quantidade_de_Pedidos", isMonetary: false, decimalPlaces: 0 },
                          { key: "quantidade_vendida", isMonetary: true, decimalPlaces: 0 },
                          { key: "Total", isMonetary: true, decimalPlaces: 2 },
                          { key: "Total em Vendas", isMonetary: true, decimalPlaces: 2 },
                          { key: "Valor Total", isMonetary: true, decimalPlaces: 2 },
                          { key: "valor_total_sem_desconto", isMonetary: true, decimalPlaces: 2 },
                          { key: "valor_total_com_desconto", isMonetary: true, decimalPlaces: 2 },
                          { key: "Valor_total", isMonetary: true, decimalPlaces: 2 },
                          { key: "total_vendas", isMonetary: true, decimalPlaces: 2 },
                          { key: "subtotal", isMonetary: true, decimalPlaces: 2 },
                          { key: "preco_unitario", isMonetary: true, decimalPlaces: 2 },
                          { key: "estimativa de vendas no catálogo", isMonetary: true, decimalPlaces: 2 },
                          { key: "ticket Médio dos Pedidos", isMonetary: true, decimalPlaces: 2 },
                          { key: "ticket Médio dos Produtos", isMonetary: true, decimalPlaces: 2 },
                          { key: "quantidade_fotos", isMonetary: false, decimalPlaces: 0 },
                          { key: "valor_total_estoque", isMonetary: true, decimalPlaces: 2 },
                        ];

                        const matchedKey = keyMapping.find(km => column.key.includes(km.key));
                          if (matchedKey) {
                            total = data.reduce(
                              (sum, data) => {
                                const raw = (data[column.key] ? data[column.key] : 0).toString().replace(/[^\d.,-]/g, "");
                                // Se tem vírgula, é formato BR (1.234,56) → remove pontos de milhar, troca vírgula por ponto
                                // Se não tem vírgula, é número normal (1234.56) → usa direto
                                const parsed = raw.includes(",")
                                  ? Number.parseFloat(raw.replace(/\./g, "").replace(",", "."))
                                  : Number.parseFloat(raw);
                                return sum + (parsed || 0);
                              },
                              0
                            );
                          }

                          // Usage in the component
                          const formattedTotal = (() => {
                            const keysWithCurrency = [
                              "total",
                              "total_em_vendas",
                              "valor_total",
                              "valor_total_sem_desconto",
                              "preco_unitario",
                              "valor_total_com_desconto",
                              "valor_total",
                              "estimativa_de_vendas_no_catálogo",
                              "total_vendas",
                              "ticket_médio_dos_pedidos",
                              "ticket_médio_dos_produtos",
                            ];

                            const normalizedKey = column.key.toLowerCase().replace(/ /g, "_").replace(/"/g, "");
                            const isCurrency = keysWithCurrency.includes(normalizedKey);
                            const decimalPlaces = matchedKey ? matchedKey.decimalPlaces : 0;

                            return formatValue(total, decimalPlaces, isCurrency);
                          })();

                        if (index === 0) {
                          const productColumns = ['referencia', 'sku', 'produto', 'nome', 'Produto', 'SKU', 'Referencia'];
                          const hasProductColumn = columns.some(col =>
                            productColumns.some(pc => col.key.toLowerCase().includes(pc.toLowerCase()))
                          );

                          return (
                            <td key={column.key}>
                              Total geral:{hasProductColumn ? ` ${data.length} produtos` : ''}
                            </td>
                          );
                        } else if (matchedKey) {
                          if (matchedKey.isMonetary) {
                            return (
                              <td key={column.key}>
                                {formattedTotal}
                              </td>
                            );
                          } else {
                            return (
                              <td key={column.key}>
                                {formattedTotal}
                              </td>
                            );
                          }
                        }
                        return <td key={column.key}></td>;
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        ) : (
          <>
            <div className="mensagem">
              <div className="texto-titulo fonteSubtitulo">
                <div>Nenhum resultado encontrado para sua busca.</div>
              </div>
              <div className="texto-subtitulo fonteRotulos">
                <div>Tente novamente com uma busca diferente!</div>
              </div>
            </div>
          </>
        )}
      </TableMainContainer>
    </>
  );
};

export default Table;
